import { yup } from 'common/validation/initYup';
import { ValidationSchema } from 'common/validation/ValidationSchema';
import { Type, Transform, plainToClass, classToPlain } from 'class-transformer';
import { TypeTransformDecimal } from 'common/dto/shared/types/TypeTransformDecimal';
import { Decimal } from 'decimal.js-light';
import { ApiProperty } from '@nestjs/swagger';
import type { IDto, IDtoPartial } from 'common/dto/shared/DtoInterfaces';
import { GenerateDto } from "cli/generate/decorators/GenerateDecorators";
import { SchemaPageNumber, SchemaPageSize, SchemaLabel } from "cli/generate/decorators/SchemaDecorators";
import { ColumnSortDirections } from 'client/ui/table/TableColumnSort';
import { MandateState } from 'common/schema/mandate/MandateTypes';

@GenerateDto()
class MandateQuery {
  @SchemaPageNumber()
  pageNumber!: number;
  @SchemaPageSize()
  pageSize!: number;
  @SchemaLabel('Stato')
  state?: MandateState | null;
  @SchemaLabel('Pagamento effettuato')
  paymentExecuted?: boolean | null;
  @SchemaLabel('Numero mandato')
  mandateNumber?: number | null;
  @SchemaLabel('Anno mandato')
  mandateYear?: number | null;
  @SchemaLabel('Non spedito')
  notShipped?: boolean;
  @SchemaLabel('Spedizione Esclusa')
  excludeShipmentId?: number | null;
  @SchemaLabel("Ordinamento")
  orderBy?: string = 'importedAt';
  @SchemaLabel("Direzione di Ordinamento")
  orderByDirection?: ColumnSortDirections = ColumnSortDirections.descend;
  @SchemaLabel('Beneficiario')
  beneficiary?: string | null;
}

/**
 * Rappresentazione DTO della classe MandateQuery 
 * Hash: 66c3b0c9fe114968b479569ee479902b
 */
@ValidationSchema(() => MandateQuerySchema)
export class MandateQueryDto {
  @ApiProperty({ required: false, type: Number, description: 'Numero di pagina' })
  pageNumber!: number;
  @ApiProperty({ required: false, type: Number, description: 'Dimensione pagina' })
  pageSize!: number;
  @ApiProperty({ required: false, enum: ['Pending', 'Paid', 'Canceled', 'Manual', 'Imported'], description: 'Stato' })
  state?: MandateState | null | undefined;
  @ApiProperty({ required: false, type: Boolean, description: 'Pagamento effettuato' })
  paymentExecuted?: boolean | null | undefined;
  @ApiProperty({ required: false, type: Number, description: 'Numero mandato' })
  mandateNumber?: number | null | undefined;
  @ApiProperty({ required: false, type: Number, description: 'Anno mandato' })
  mandateYear?: number | null | undefined;
  @ApiProperty({ required: false, type: Boolean, description: 'Non spedito' })
  notShipped?: boolean | undefined;
  @ApiProperty({ required: false, type: Number, description: 'Spedizione Esclusa' })
  excludeShipmentId?: number | null | undefined;
  @ApiProperty({ required: false, type: String, description: 'Ordinamento' })
  orderBy?: string | undefined = 'importedAt';
  @ApiProperty({ required: false, enum: ['ASC', 'DESC'], description: 'Direzione di Ordinamento' })
  orderByDirection?: ColumnSortDirections | undefined = ColumnSortDirections.descend;
  @ApiProperty({ required: false, type: String, description: 'Beneficiario' })
  beneficiary?: string | null | undefined;

  /** Proprietà che identifica i DTO */
  readonly __dto!: any;

  /**
   * Crea una nuova istanza con i valori forniti
   */
  constructor(values?: IDtoPartial<MandateQueryDto>) {
    if (values != null) {
      Object.assign(this, values instanceof MandateQueryDto ? values : plainToClass(MandateQueryDto, values));
    }
  }

  async validate(options?: any) {
    const validated = await MandateQuerySchema.validate(classToPlain(this), options);
    return new MandateQueryDto(validated);
  }
}

export const MandateQuerySchema = yup
  .object({
    pageNumber: yup.number().default(1).min(1).integer().positive().label('Numero di pagina'),
    pageSize: yup.number().default(20).min(10).max(50).integer().positive().label('Dimensione pagina'),
    state: yup.string().oneOfEnum(MandateState).nullable().label('Stato'),
    paymentExecuted: yup.boolean().nullable().label('Pagamento effettuato'),
    mandateNumber: yup.number().nullable().label('Numero mandato'),
    mandateYear: yup.number().nullable().label('Anno mandato'),
    notShipped: yup.boolean().label('Non spedito'),
    excludeShipmentId: yup.number().nullable().label('Spedizione Esclusa'),
    orderBy: yup.string().default('importedAt').label('Ordinamento'),
    orderByDirection: yup.string().oneOfEnum(ColumnSortDirections).default(ColumnSortDirections.descend).label('Direzione di Ordinamento'),
    beneficiary: yup.string().nullable().label('Beneficiario')
  })
  .noUnknown()
  .meta({ schemaName: "MandateQuerySchema" })
  .required();
